import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import ParentSidebar from "../components/ParentSidebar";
import BottomNav from "../components/BottomNav";
import { useTheme } from "../hooks/useTheme";
import type { Room, Difficulty, Completion } from "@starquest/core";
import styles from "./ParentHome.module.css";
import own from "./HideStar.module.css";

const DEFAULTS: Record<Difficulty, { pointReward: number; xp: number }> = {
  common:    { pointReward: 5,  xp: 20  },
  rare:      { pointReward: 15, xp: 50  },
  epic:      { pointReward: 30, xp: 100 },
  legendary: { pointReward: 50, xp: 200 },
};

export default function HideStar() {
  const navigate    = useNavigate();
  const { familyId } = useAuthStore();
  const { completions, rooms, setCompletions, setRooms } = useFamilyStore();

  const theme = useTheme(); // no kid arg → base theme

  const DIFFICULTIES: { value: Difficulty; label: string; emoji: string; hex: string }[] = [
    { value: "common" as const,    label: theme.difficulty.common,    emoji: "⭐", hex: "#9ca3af" },
    { value: "rare" as const,      label: theme.difficulty.rare,      emoji: "💎", hex: "#3b82f6" },
    { value: "epic" as const,      label: theme.difficulty.epic,      emoji: "🔮", hex: "#8b5cf6" },
    { value: "legendary" as const, label: theme.difficulty.legendary, emoji: "🌟", hex: "#f59e0b" },
  ];

  const [roomId,            setRoomId]            = useState("");
  const [difficulty,        setDifficulty]        = useState<Difficulty>("common");
  const [emoji,             setEmoji]             = useState("⭐");
  const [challengeName,     setChallengeName]     = useState("");
  const [challengeDescription, setChallengeDescription] = useState("");
  const [pointReward,       setPointReward]       = useState(5);
  const [xp,                setXp]                = useState(20);
  const [timeLimit,         setTimeLimit]         = useState(30);
  const [saving,            setSaving]            = useState(false);
  const [done,              setDone]              = useState(false);
  const [error,             setError]             = useState("");

  useEffect(() => {
    if (!familyId) return;
    api.listRooms(familyId).then((r) => setRooms(r as Room[]));
    api.listCompletions(familyId, "pending").then((c) => setCompletions(c as Completion[]));
  }, [familyId]);

  function pickDifficulty(d: Difficulty) {
    setDifficulty(d);
    setPointReward(DEFAULTS[d].pointReward);
    setXp(DEFAULTS[d].xp);
    setEmoji(DIFFICULTIES.find((x) => x.value === d)!.emoji);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!familyId) return;
    if (!roomId) { setError("Pick a room first"); return; }
    if (!challengeName.trim()) { setError(`${theme.challengeName} name is required`); return; }
    setError(""); setSaving(true);
    try {
      await api.saveChallenge(familyId, {
        roomId, difficulty, emoji,
        challengeName: challengeName.trim(),
        challengeDescription: challengeDescription.trim(),
        pointReward, xp, timeLimitMinutes: timeLimit,
      });
      setDone(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to hide star");
    } finally { setSaving(false); }
  }

  function resetForm() {
    setDone(false); setRoomId(""); setDifficulty("common");
    setEmoji("⭐"); setChallengeName(""); setChallengeDescription("");
    setPointReward(5); setXp(20); setTimeLimit(30); setError("");
  }

  const visibleRooms = rooms.filter((r) => r.isVisible);

  return (
    <div className={styles.page}>
      <ParentSidebar pendingCount={completions.length} />
      <main className={styles.main}>
        <div className={styles.head}>
          <div>
            <h1 className={styles.h1}>🌟 {`Create a ${theme.challengeName}`}</h1>
            <div className={styles.sub}>Create a {theme.challengeName.toLowerCase()} and hide it in a room for your {(theme.kidRole + "s").toLowerCase()}</div>
            <BottomNav />
    </div>
          <BottomNav />
    </div>

        {done ? (
          <div className={own.success}>
            <div className={own.successIcon}>🌟</div>
            <div className={own.successTitle}>Star hidden!</div>
            <p className={own.successDesc}>Your explorers will see it on the room map.</p>
            <div className={own.successBtns}>
              <button className={`${styles.btn} ${styles.primary}`} onClick={resetForm}>Hide another</button>
              <button className={`${styles.btn} ${styles.ghost}`} onClick={() => navigate("/parent")}>Back to Command Center</button>
              <BottomNav />
    </div>
            <BottomNav />
    </div>
        ) : (
          <form className={own.form} onSubmit={handleSubmit}>

            <div className={own.section}>
              <div className={own.label}>Which room?</div>
              {visibleRooms.length === 0 ? (
                <p className={own.hint}>
                  No rooms yet —{" "}
                  <span className={own.textLink} onClick={() => navigate("/parent/builder")}>
                    build your house first
                  </span>
                </p>
              ) : (
                <div className={own.roomGrid}>
                  {visibleRooms.map((r) => (
                    <button
                      key={r.roomId} type="button"
                      className={`${own.roomBtn} ${roomId === r.roomId ? own.roomActive : ""}`}
                      onClick={() => setRoomId(r.roomId)}
                    >
                      <span className={own.roomEmoji}>{r.emoji}</span>
                      <span className={own.roomName}>{r.name}</span>
                    </button>
                  ))}
                  <BottomNav />
    </div>
              )}
              <BottomNav />
    </div>

            <div className={own.section}>
              <div className={own.label}>Difficulty</div>
              <div className={own.rarityRow}>
                {DIFFICULTIES.map((d) => (
                  <button
                    key={d.value} type="button"
                    className={`${own.rarityBtn} ${difficulty === d.value ? own.rarityActive : ""}`}
                    style={difficulty === d.value ? { borderColor: d.hex, background: `${d.hex}22` } : {}}
                    onClick={() => pickDifficulty(d.value)}
                  >
                    <span>{d.emoji}</span>
                    <span>{d.label}</span>
                  </button>
                ))}
                <BottomNav />
    </div>
              <BottomNav />
    </div>

            <div className={own.section}>
              <div className={own.label}>{`${theme.challengeName} name *`}</div>
              <input
                className={own.input}
                placeholder="e.g. Tidy the Treasure Chamber"
                value={challengeName}
                onChange={(e) => setChallengeName(e.target.value)}
                maxLength={80}
              />
              <BottomNav />
    </div>

            <div className={own.section}>
              <div className={own.label}>Description (optional)</div>
              <textarea
                className={own.textarea}
                placeholder={`What does the ${theme.kidRole.toLowerCase()} need to do to complete this ${theme.challengeName.toLowerCase()}?`}
                value={challengeDescription}
                onChange={(e) => setChallengeDescription(e.target.value)}
                maxLength={300}
                rows={3}
              />
              <BottomNav />
    </div>

            <div className={own.tripleRow}>
              <div className={own.section}>
                <div className={own.label}>{`${theme.pointEmoji} ${theme.pointName} reward`}</div>
                <input type="number" className={own.input} value={pointReward} min={1} max={999}
                  onChange={(e) => setPointReward(Math.max(1, +e.target.value || 1))} />
                <BottomNav />
    </div>
              <div className={own.section}>
                <div className={own.label}>✨ XP</div>
                <input type="number" className={own.input} value={xp} min={1} max={999}
                  onChange={(e) => setXp(Math.max(1, +e.target.value || 1))} />
                <BottomNav />
    </div>
              <div className={own.section}>
                <div className={own.label}>⏱ Time limit (min)</div>
                <input type="number" className={own.input} value={timeLimit} min={1} max={1440}
                  onChange={(e) => setTimeLimit(Math.max(1, +e.target.value || 1))} />
                <BottomNav />
    </div>
              <BottomNav />
    </div>

            {error && <p className={own.error}>{error}</p>}

            <div className={own.footer}>
              <button type="submit" className={`${styles.btn} ${styles.primary}`}
                disabled={saving || !roomId || !challengeName.trim()}>
                {saving ? "Creating…" : `🌟 Create ${theme.challengeName}`}
              </button>
              <button type="button" className={`${styles.btn} ${styles.ghost}`}
                onClick={() => navigate("/parent")}>Cancel</button>
              <BottomNav />
    </div>
          </form>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
